import { TicketData, PrinterProfile } from '../types/printerProfile';
import { buildAdvancedTicket } from './ticketFormatterAdvanced';
import { formatOrderNumber } from './orderNumberFormatter';

export const buildCustomTicket = (profile: PrinterProfile, data: TicketData): string => {
  if (profile.config.advancedTemplate) {
    return buildAdvancedTicket(data, profile);
  }
  const {
    storeInfo,
    orderCode,
    items,
    orderType,
    tableNumber,
    customerName,
    orderTotals,
    paymentMethod,
    taxResult,
    t,
    loyaltyDiscount = 0,
    loyaltyPointsUsed = 0,
    happyHourDiscount = 0,
    happyHourPercent = 0,
  } = data;

  const config = profile.config;
  const width = config.width || 42;
  const separator = config.separator || '----------------';
  const paddingWidth = config.paddingWidth || 20;

  const lines: string[] = [];

  const getCommand = (type: 'large' | 'medium' | 'normal'): string => {
    if (!config.useEscPos || !config.commands) return '';
    const cmd = config.commands[type];
    if (!cmd) return '';
    return cmd.replace(/\\x1B/g, '\x1B').replace(/\\x21/g, '\x21').replace(/\\x18/g, '\x18').replace(/\\x08/g, '\x08').replace(/\\x00/g, '\x00');
  };

  const getAlignment = (type: 'center' | 'left' | 'right'): string => {
    if (!config.alignmentTags) return '';
    return config.alignmentTags[type] || '';
  };

  const centerText = (text: string): string => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return ' '.repeat(padding) + text;
  };

  const formatLine = (left: string, right: string): string => {
    const space = Math.max(2, width - left.length - right.length);
    return left + ' '.repeat(space) + right;
  };

  lines.push(getCommand('large'));
  lines.push(getAlignment('center') + '=== ** ' + (storeInfo?.name.toUpperCase() || t('Restaurant par défaut')) + ' ** ===');
  lines.push('');

  lines.push(getCommand('medium') + getAlignment('center') + '**** ' + t('Commande numéro') + ' #' + formatOrderNumber(orderCode) + ' ****');
  lines.push('');

  lines.push(getCommand('normal') + getAlignment('left') + t('Date') + ': ' + new Date().toLocaleString('fr-FR'));

  const orderTypeStr = orderType === 'takeaway' ? t('À emporter') : t('Sur place');
  const typeText = t('Type') + ': **' + orderTypeStr + '**' + (tableNumber ? ` - ${t('Table')} ${tableNumber}` : '');
  lines.push(getAlignment('left') + typeText);

  if (customerName) {
    lines.push(getAlignment('left') + t('Client') + ': ' + customerName);
  }

  lines.push(getAlignment('center') + separator);

  for (const item of items) {
    const itemLine = `${item.quantity}x ${item.menuItem.name.padEnd(paddingWidth, ' ')}`;
    const price = t('Prix', { price: (item.menuItem.price * item.quantity).toFixed(2) });
    lines.push(getAlignment('left') + itemLine + getAlignment('right') + price);

    if (config.aggregateOptions !== false) {
      for (const [, options] of Object.entries(item.customizations)) {
        for (const opt of options) {
          const optText = `  - ${opt.name}${opt.price > 0 ? ` (+${t('Prix ajout', { price: opt.price.toFixed(2) })})` : ''}`;
          lines.push(getAlignment('left') + optText);
        }
      }
    }
  }

  lines.push(getAlignment('center') + separator);

  if (config.showTaxDetails !== false) {
    const subtotalHT = (taxResult?.subtotalHT ?? orderTotals.subtotal).toFixed(2);
    const subtotalLine = formatLine(
      t('Sous-total HT') + ':',
      t('Prix', { price: subtotalHT })
    );
    lines.push(getAlignment('left') + subtotalLine);

    const taxAmount = (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2);
    const taxRateText = taxResult?.taxRate !== null && taxResult?.taxRate !== undefined
      ? t('TVA', { rate: (taxResult.taxRate % 1 === 0 ? taxResult.taxRate.toFixed(0) : taxResult.taxRate.toFixed(1)) })
      : 'TVA';
    const taxLine = formatLine(
      taxRateText + ':',
      t('Prix', { price: taxAmount })
    );
    lines.push(getAlignment('left') + taxLine);
    lines.push('');
  }

  // v5 happy hour: ligne séparée (NF525)
  if (happyHourDiscount > 0) {
    const hhLine = formatLine(
      `${t('Happy Hour')} -${happyHourPercent}%:`,
      `-${t('Prix', { price: happyHourDiscount.toFixed(2) })}`
    );
    lines.push(getAlignment('left') + hhLine);
    lines.push('');
  }

  // v5 fidélité: ligne séparée pour la réduction si applicable
  if (loyaltyDiscount > 0) {
    const reductionLine = formatLine(
      `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
      `-${t('Prix', { price: loyaltyDiscount.toFixed(2) })}`
    );
    lines.push(getAlignment('left') + reductionLine);
    lines.push('');
  }

  const totalTTCBefore = taxResult?.totalTTC ?? orderTotals.invoiceTotal;
  const totalTTC = Math.max(0, totalTTCBefore - loyaltyDiscount - happyHourDiscount).toFixed(2);
  lines.push(getCommand('medium') + getAlignment('left') + formatLine(
    t('Total TTC') + ':',
    t('Prix', { price: totalTTC })
  ));
  lines.push('');

  const paymentStr = paymentMethod
    ? t(paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1))
    : t('Non spécifié');
  lines.push(getCommand('normal') + getAlignment('left') + formatLine(
    t('Paiement') + ':',
    '**' + paymentStr + '**'
  ));

  lines.push(getAlignment('center') + separator);
  lines.push(getAlignment('center') + t('Merci de votre visite'));
  lines.push('');

  return lines.join('\n');
};
